import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export const normalizeLang = (value?: string | null) =>
  value?.toLowerCase().startsWith('en') ? 'en' : 'fr';

/**
 * Persiste la langue choisie sur le profil de l'utilisateur connecté
 * et applique la langue enregistrée lors de la connexion.
 */
export const useLanguageSync = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const appliedForUser = useRef<string | null>(null);

  // À la connexion : appliquer la langue enregistrée sur le profil
  useEffect(() => {
    if (!user) {
      appliedForUser.current = null;
      return;
    }
    if (appliedForUser.current === user.id) return;
    appliedForUser.current = user.id;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('profils_utilisateur')
        .select('langue')
        .eq('id', user.id)
        .maybeSingle();

      if (cancelled || error || !data) return;
      const stored = normalizeLang(data.langue);
      if (stored !== normalizeLang(i18n.language)) {
        i18n.changeLanguage(stored);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, i18n]);

  // Au changement de langue : enregistrer sur le profil
  useEffect(() => {
    if (!user) return;

    const handler = (lng: string) => {
      const langue = normalizeLang(lng);
      supabase
        .from('profils_utilisateur')
        .update({ langue })
        .eq('id', user.id)
        .then(({ error }) => {
          if (error) console.error('Langue non enregistrée:', error.message);
        });
    };

    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, [user, i18n]);
};

export default useLanguageSync;
